import { formatPercent, formatStat } from './format';
import type { Headline } from './overview';
import type { DistributionItem, QuestionSummary } from './summarize';

/**
 * Plain one-line captions for cards and headline numbers. Fixed templates
 * filled from deterministic summaries: never model text, never adjectives that
 * judge the result.
 */

/** Statistics below this n are not shown, so numbers get no caption. */
const STAT_MIN_N = 3;

/** Items sharing the highest count (none when nobody answered). */
function topItems(items: readonly DistributionItem[]): DistributionItem[] {
  const max = Math.max(0, ...items.map(item => item.count));
  return max > 0 ? items.filter(item => item.count === max) : [];
}

/** Caption for a tie at the top. */
function tieCaption(tied: DistributionItem[]): string {
  const share = formatPercent(tied[0].share);
  return tied.length === 2 ? `${tied[0].label} and ${tied[1].label} tie at ${share}` : `${tied.length} answers tie at ${share}`;
}

/** One plain caption for a question summary, or null when the card needs none. */
export function captionFor(summary: QuestionSummary): string | null {
  switch (summary.kind) {
    case 'distribution': {
      if (summary.source === 'boolean') {
        const checked = summary.items.find(item => item.key === 'true')?.count ?? 0;
        return summary.answered ? `${checked} of ${summary.answered} checked` : null;
      }
      const tied = topItems(summary.items);
      if (!tied.length) return null;
      if (tied.length > 1) return tieCaption(tied);
      const [top] = tied;
      if (summary.multi) return `${top.label} picked by ${formatPercent(top.share)}`;
      return top.share >= 0.5 ? `${top.count} of ${summary.answered} chose ${top.label}` : `${top.label} leads with ${formatPercent(top.share)}`;
    }
    case 'numeric':
      return summary.n >= STAT_MIN_N ? `Typically ${formatStat(summary.median)} · ${formatStat(summary.min)} to ${formatStat(summary.max)}` : null;
    case 'nps':
      return summary.answered ? `NPS ${Math.round(summary.score)}` : null;
    case 'ranking': {
      const first = summary.items.reduce<typeof summary.items[number] | null>((best, item) => !best || item.firstPlace > best.firstPlace ? item : best, null);
      return first && first.firstPlace > 0 ? `${first.label} ranks first most often` : null;
    }
    default:
      return null;
  }
}

/** Captions under the three headline numbers. */
export interface HeadlineCaptions {
  responses: string | null;
  completed: string | null;
  time: string | null;
}

/** Plain captions for the headline cards. */
export function headlineCaptions(headline: Headline): HeadlineCaptions {
  const unfinished = headline.responses - headline.completed;
  return {
    responses: headline.responses ? (unfinished ? `${unfinished} not finished` : 'All finished') : null,
    completed: headline.responses ? `${headline.completed} of ${headline.responses}` : null,
    time: headline.medianMinutes === null ? null : 'Median per response',
  };
}
